


import { Grid } from "@mui/material";
import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { breadCrumbsStore } from "../../Components/BreadCrumbs/BreadCrumbsListner";
import { callAPI, Method, updateMainHeader, dateFormat } from "../../commonFunctions/CommonFunctions";
import { useSnackbar } from "../../Components/Snackbar/SnackBarContext";
import { useDispatch } from "react-redux";
import WSDataGrid, { IColumns } from "../../Components/DataGrid/WSDataGrid";
import { IMetaData } from "../../Components/Dialog/WSCommonDialog";
import { openPopUp } from "../../Components/Dialog/OpenNewModal";

import WSButton from "../../Components/Button/WSButton";
import AddIcon from "@mui/icons-material/Add";
import WSFilterButtonGroup from "../../Components/Filter/WSFilterButtonGroup";
import LoanAccount from "./LoanAccount";
import ConfirmationPopup from "../../Components/Confirmation/Confirmation";
import { resetDataComponent } from "../../StateManagement/actions";
import { useForm, FormProvider, useFormContext } from "react-hook-form";

const LoanAccountWrapper: React.FC<{ id?: string }> = ({ id }) => {
  const methods = useFormContext();
  const { control, register, formState: { errors }, setValue, reset } = methods;

  return (
    <LoanAccount
      control={control}
      errors={errors}
      register={register}
      id={id}
      setValue={setValue}
      reset={reset}
    />
  );
};

export const LoanAccountList: React.FC<any> = React.memo((props) => {
  const filters = ["All Records"];

  const [isPopupOpen, setPopupOpen] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState(filters[0]);
  const [loading, setLoading] = useState(false);

  // **CRITICAL FIX**: Use refs for immediate access (no stale closure)
  const selectedIdRef = useRef<string>("");
  const methods = useForm<any>();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { showMessage } = useSnackbar();

  useEffect(() => {
    updateMainHeader("Loan Account");
  });

  breadCrumbsStore.updateBreadCrumbs([
    { "Loan Account List": "/loan-account/list" },
  ]);

  const source = {
    url: "Vyapar/LoanAccount/search",
    page: 1,
    pageSize: 10,
    filter: [],
    sort: [],
  };

  const handleAction = useCallback(async (action: string, row: any) => {
    if (action === "Edit") {
      try {
        const response = await callAPI(
          `Vyapar/LoanAccount/${row.id}`,
          Method.GET
        );

        if (response.data) {
          const data = response.data;

          // **IMMEDIATE**: Set ref FIRST (no state delay)
          selectedIdRef.current = row.id;

          // Populate form
          methods.reset({
            accountName: data.accountName || "",
            lenderBank: data.lenderBank || "",
            accountNumber: data.accountNumber || "",
            currentBalance: data.currentBalance || 0,
            balanceasof: data.balanceAsOf
              ? dateFormat(new Date(data.balanceAsOf), 'DD-MM-YYYY HH:mm:ss')
              : dateFormat(new Date(), 'DD-MM-YYYY HH:mm:ss'),
            loanReceivedIn: data.loanReceivedIn || "",
            interestRate: data.interestRate || 0,
            termDuration: data.termDuration || "",
            processingFee: data.processingFee || 0,
            processingFeePaidFrom: data.processingFeePaidFrom || "",
            description: data.description || "",
          });

          setTimeout(() => {
            openPopUp(editMetadata, handleData, "60rem");
          }, 50);
        }
      } catch (error) {
        console.error("Failed to fetch loan account:", error);
        showMessage("Failed to fetch loan account data", "error");
      }
    }
    else if (action === "Delete") {
      selectedIdRef.current = row.id;
      setPopupOpen(true);
    }
  }, [methods, showMessage]);

  const columns: IColumns[] = [
    {
      header: "Account Name",
      fieldName: "accountName",
    },
    {
      header: "Lender Bank",
      fieldName: "lenderBank",
    },
    {
      header: "Account Number",
      fieldName: "accountNumber",
      tooltip: true,
    },
    {
      header: "Current Balance",
      fieldName: "currentBalance",
    },
    {
      header: "Balance As Of",
      fieldName: "balanceAsOf",
      type: "date",
    },
    {
      header: "Interest Rate (%)",
      fieldName: "interestRate",
    },
    {
      header: "Term Duration",
      fieldName: "termDuration",
    },
    {
      header: "Actions",
      fieldName: "actions",
      type: "action",
      actions: ["Edit", "Delete"],
      actionCallback: handleAction,
    },
  ];

  const handleClick = useCallback(() => {
    // **IMMEDIATE**: Clear ref for create
    selectedIdRef.current = "";

    methods.reset({
      accountName: "",
      lenderBank: "",
      accountNumber: "",
      currentBalance: "",
      balanceasof: dateFormat(new Date(), 'DD-MM-YYYY HH:mm:ss'),
      loanReceivedIn: "",
      interestRate: "",
      termDuration: "",
      processingFee: "",
      processingFeePaidFrom: "",
      description: "",
    });

    setTimeout(() => {
      openPopUp(createMetadata, handleData, "60rem");
    }, 100);
  }, [methods]);

  const handleData = useCallback(async () => {
    try {
      setLoading(true);

      const formData = methods.getValues();

      const request = {
        accountName: formData.accountName || "",
        lenderBank: formData.lenderBank || "",
        accountNumber: formData.accountNumber || "",
        currentBalance: Number(formData.currentBalance) || 0,
        balanceAsOf: formData.balanceasof
          ? new Date(formData.balanceasof).toISOString()
          : new Date().toISOString(),
        loanReceivedIn: formData.loanReceivedIn || "",
        interestRate: Number(formData.interestRate) || 0,
        termDuration: formData.termDuration || "",
        processingFee: Number(formData.processingFee) || 0,
        processingFeePaidFrom: formData.processingFeePaidFrom || "",
        description: formData.description || "",
      };

      let response;

      // **FIXED**: Use REF - always current value
      if (selectedIdRef.current) {
        response = await callAPI(
          `Vyapar/LoanAccount/${selectedIdRef.current}`,
          Method.PUT,
          request
        );
      } else {
        response = await callAPI(
          `Vyapar/LoanAccount`,
          Method.POST,
          request
        );
      }
      if (response && response.success === true) {
        dispatch(resetDataComponent("loanAccountGrid"));  // ← ADDED HERE
        showMessage(response?.message, "success");
      } else {
        showMessage(response?.message || "Operation failed", "error");
      }


      if (response) {
        showMessage(
          response?.message,
          response.success ? "success" : "error"
        );
      }

    } catch (error) {
      console.error("Failed to save loan account:", error);
      showMessage("Failed to save loan account", "error");
    } finally {
      setLoading(false);
    }
  }, [methods, showMessage]);

  const createMetadata = useMemo<IMetaData>(() => ({
    title: "Loan Account",
    hideActions: false,
    content: () => (
      <FormProvider {...methods}>
        <LoanAccountWrapper />
      </FormProvider>
    ),
    actions: [
      {
        label: "Cancel",
        className: "ws-cancel",
        type: "button",
      },
      {
        label: "Save",
        type: "submit",
        onClick: handleData,
        autoFocus: true,
      },
    ],
    isDragable: true,
  }), [methods, handleData]);

  const editMetadata = useMemo<IMetaData>(() => ({
    title: "Edit Loan Account",
    hideActions: false,
    content: () => (
      <FormProvider {...methods}>
        <LoanAccountWrapper id={selectedIdRef.current} />
      </FormProvider>
    ),
    actions: [
      {
        label: "Cancel",
        className: "ws-cancel",
        type: "button",
      },
      {
        label: "Update",
        type: "submit",
        onClick: handleData,
        autoFocus: true,
      },
    ],
    isDragable: true,
  }), [methods, handleData]);

  return (
    <Grid p={3}>
      <Grid container spacing={2}>
        
        <Grid item xs={12}>
          <WSDataGrid
            id="loanAccountGrid"
            title="Loan Account"
            columns={columns}
            source={source}
            searchBox={{ fieldName: 'name' }}
            addButton={{ label: "Add New Loan", color: "secondary" }}
            onAddButtonClick={handleClick}

            gridHeaderSx={{
              backgroundColor: "whitesmoke",
              border: "1px solid whitesmoke",
            }}
          />
        </Grid>

        <ConfirmationPopup
          type="DELETE"
          apiUrl="Vyapar/LoanAccount"
          id={selectedIdRef.current}
          confirmMessage="Are you sure you want to delete this loan accont?"
          open={isPopupOpen}
          onClose={() => setPopupOpen(false)}
          onConfirm={(data: any) => {
            dispatch(resetDataComponent("loanAccountGrid"));
            if (data.success === true) {
              showMessage(data?.message, "success");
            } else {
              showMessage(data?.message, "error");
            }
          }}
        />
      </Grid>
    </Grid>
  );
});
